import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Building2, Calendar, CheckCircle2, ChevronRight, Clock, Download, Edit, Filter, Mail, MapPin, MoreVertical, Phone, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import FacilityTypeSelector from '../components/FacilityTypeSelector';
import SingleLocationSelector from '../components/SingleLocationSelector';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/DropdownMenu';
import { Input } from '../components/ui/Input';
import { Pagination } from '../components/ui/Pagination';
import { Skeleton } from '../components/ui/Skeleton';
import { Table, TableColumn } from '../components/ui/Table';
import useLocale from '../contexts/LocaleContext';
import { getCurrentUser } from '../hooks/useAuth';
import {
  useCreateFacility,
  useDeactivateFacility,
  useFacilities,
  useFacilityTypes,
  useUpdateFacility,
} from '../hooks/useFacilities';
import { assessmentsApi, facilitiesApi, usersApi } from '../lib/api-client';
import { hasPermission } from '../lib/permissions';
import { getLocationLevelName } from '../lib/utils';
import type { AssessmentRecommendationDto, CreateFacilityDto, FacilityDto, PendingRecommendationsDto } from '../types/api';

const facilitySchema = z.object({
  title: z.string().min(1, 'Title is required').max(255, 'Title must be less than 255 characters'),
  identifierNumber: z.string().min(1, 'Identifier number is required'),
  contactNumber: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(val => !val || val === '' || /^\+?[\d\s-()]+$/.test(val), 'Please enter a valid phone number'),
  nidNumber: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(val => !val || val === '' || (val.length === 16 && /^\d{16}$/.test(val)), 'NID must be exactly 16 digits'),
  email: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(val => !val || val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), 'Please enter a valid email address'),
  locationId: z.string().optional().or(z.literal('')),
  facilityTypeId: z.string().optional().or(z.literal('')),
});

type FacilityForm = z.infer<typeof facilitySchema>;

// ── Facility Detail Drawer ────────────────────────────────────────────────────

const SCORE_COLOR = (s: number) =>
  s >= 70 ? 'text-green-700 bg-green-50 border-green-200' :
  s >= 50 ? 'text-yellow-700 bg-yellow-50 border-yellow-200' :
            'text-red-700 bg-red-50 border-red-200';

function RecommendationRow({ rec, onToggle }: { rec: AssessmentRecommendationDto; onToggle: (id: string, resolved: boolean) => void }) {
  const isPast = rec.deadline && new Date(rec.deadline) < new Date();
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${rec.resolved ? 'bg-gray-50 border-gray-200 opacity-60' : isPast ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
      <button
        onClick={() => onToggle(rec.id, !rec.resolved)}
        className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${rec.resolved ? 'bg-green-500 border-green-500 text-white' : 'border-gray-400 hover:border-green-500'}`}
        title={rec.resolved ? 'Mark as pending' : 'Mark as resolved'}
      >
        {rec.resolved && <CheckCircle2 className='w-3 h-3' />}
      </button>
      <div className='flex-1 min-w-0'>
        <p className={`text-sm ${rec.resolved ? 'line-through text-gray-400' : 'text-gray-800'}`}>{rec.recommendation}</p>
        {rec.deadline && (
          <div className={`flex items-center gap-1 mt-1 text-xs ${rec.resolved ? 'text-gray-400' : isPast ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
            <Clock className='w-3 h-3' />
            <span>Due {new Date(rec.deadline).toLocaleDateString()}</span>
            {isPast && !rec.resolved && <span className='ml-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-semibold'>Overdue</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function FacilityDrawer({ facility, onClose, locale }: { facility: FacilityDto; onClose: () => void; locale: string }) {
  const queryClient = useQueryClient();

  const { data: recentAssessments, isLoading: loadingAssessments } = useQuery({
    queryKey: ['facility-assessments', facility.id],
    queryFn: () => assessmentsApi.getAll({ facilityId: facility.id, limit: 8 }),
    staleTime: 1000 * 60 * 2,
  });

  const { data: hygieneRecs, isLoading: loadingHygiene } = useQuery<PendingRecommendationsDto>({
    queryKey: ['pending-recommendations', facility.id, 'HYGIENE'],
    queryFn: () => assessmentsApi.getPendingRecommendations(facility.id, { assessmentType: 'HYGIENE' }),
    staleTime: 1000 * 60 * 2,
  });

  const { data: complianceRecs, isLoading: loadingCompliance } = useQuery<PendingRecommendationsDto>({
    queryKey: ['pending-recommendations', facility.id, 'COMPLIANCE'],
    queryFn: () => assessmentsApi.getPendingRecommendations(facility.id, { assessmentType: 'COMPLIANCE' }),
    staleTime: 1000 * 60 * 2,
  });

  const handleToggleResolved = async (recommendationId: string, resolved: boolean) => {
    try {
      await assessmentsApi.updateRecommendationResolved(recommendationId, resolved);
      queryClient.invalidateQueries({ queryKey: ['pending-recommendations', facility.id] });
      toast.success(resolved ? 'Marked as resolved' : 'Marked as pending');
    } catch {
      toast.error('Failed to update recommendation');
    }
  };

  const locationLabel = facility.locationStack?.locationNames?.join(' › ') ?? facility.location?.name ?? null;
  const allRecs = [
    ...(hygieneRecs?.recommendations ?? []),
    ...(complianceRecs?.recommendations ?? []),
  ];
  const pendingCount = allRecs.filter(r => !r.resolved).length;

  return (
    <>
      {/* Backdrop */}
      <div className='fixed inset-0 bg-black/30 z-40' onClick={onClose} />

      {/* Drawer panel */}
      <div className='fixed inset-y-0 right-0 z-50 w-full max-w-[480px] bg-white shadow-2xl flex flex-col overflow-hidden'>
        {/* Header */}
        <div className='flex items-start justify-between px-5 py-4 border-b border-gray-200 bg-white flex-shrink-0'>
          <div className='flex items-start gap-3 min-w-0'>
            <div className='bg-secondary rounded-xl p-2.5 flex-shrink-0'>
              <Building2 className='h-5 w-5 text-primary' />
            </div>
            <div className='min-w-0'>
              <h2 className='text-base font-semibold text-gray-900 truncate'>{facility.title}</h2>
              {facility.facilityType && (
                <span className='inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 mt-0.5'>
                  {locale === 'rw' ? facility.facilityType.nameKinyarwanda : locale === 'fr' ? facility.facilityType.nameFrench : facility.facilityType.name}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className='p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2'>
            <X className='h-5 w-5' />
          </button>
        </div>

        {/* Scrollable body */}
        <div className='flex-1 overflow-y-auto p-5 space-y-5'>

          {/* Info grid */}
          <section>
            <h3 className='text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3'>Details</h3>
            <div className='grid grid-cols-2 gap-3'>
              {locationLabel && (
                <div className='col-span-2 flex items-start gap-2 text-sm text-gray-700'>
                  <MapPin className='h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0' />
                  <span>{locationLabel}</span>
                </div>
              )}
              {facility.contactNumber && (
                <div className='flex items-center gap-2 text-sm text-gray-700'>
                  <Phone className='h-4 w-4 text-gray-400 flex-shrink-0' />
                  <span>{facility.contactNumber}</span>
                </div>
              )}
              {facility.email && (
                <div className='flex items-center gap-2 text-sm text-gray-700 col-span-2 truncate'>
                  <Mail className='h-4 w-4 text-gray-400 flex-shrink-0' />
                  <span className='truncate'>{facility.email}</span>
                </div>
              )}
              {facility.identifierNumber && (
                <div className='flex items-center gap-2 text-sm text-gray-700'>
                  <span className='text-gray-400 text-xs font-mono'>#</span>
                  <span className='font-mono text-xs'>{facility.identifierNumber}</span>
                </div>
              )}
              {facility.nidNumber && (
                <div className='flex items-center gap-2 text-sm text-gray-700'>
                  <span className='text-gray-400 text-xs'>NID</span>
                  <span className='font-mono text-xs'>{facility.nidNumber}</span>
                </div>
              )}
              {facility.coordinates && (() => {
                const [lat, lng] = facility.coordinates.split(',');
                return (
                  <div className='col-span-2'>
                    <a
                      href={`https://www.google.com/maps?q=${lat},${lng}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center gap-1.5 text-xs text-primary hover:underline'
                    >
                      <MapPin className='h-3.5 w-3.5' />
                      View on Google Maps
                    </a>
                  </div>
                );
              })()}
              <div className='flex items-center gap-2 text-xs text-gray-500'>
                <Calendar className='h-3.5 w-3.5 text-gray-400' />
                <span>Added {new Date(facility.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </section>

          {/* Recent Assessments */}
          <section>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Recent Assessments</h3>
              <Link
                to={`/assessments?facilityId=${facility.id}`}
                className='text-xs text-primary hover:underline flex items-center gap-0.5'
              >
                View all <ChevronRight className='h-3 w-3' />
              </Link>
            </div>

            {loadingAssessments ? (
              <div className='space-y-2'>
                {[...Array(3)].map((_, i) => <Skeleton key={i} className='h-12 rounded-lg' />)}
              </div>
            ) : (recentAssessments?.data?.length ?? 0) === 0 ? (
              <div className='text-center py-6 text-sm text-gray-400'>No assessments yet</div>
            ) : (
              <div className='space-y-2'>
                {recentAssessments!.data.map(a => (
                  <Link
                    key={a.id}
                    to={`/assessments/${a.id}`}
                    className='flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-primary/40 hover:bg-secondary/30 transition-colors'
                  >
                    <div className='flex items-center gap-2 min-w-0'>
                      <span className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-md border ${a.assessmentType === 'COMPLIANCE' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-secondary text-primary border-primary/30'}`}>
                        {a.assessmentType ?? 'HYGIENE'}
                      </span>
                      <span className='text-xs text-gray-500 truncate'>{new Date(a.createdAt).toLocaleDateString()}</span>
                      {a.isReassessment && <span className='text-[10px] text-purple-600 font-medium'>Reassessment</span>}
                    </div>
                    <span className={`flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-md border ${SCORE_COLOR(a.percentageScore)}`}>
                      {a.percentageScore.toFixed(1)}%
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Pending Recommendations */}
          <section>
            <div className='flex items-center gap-2 mb-3'>
              <h3 className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Recommendations</h3>
              {pendingCount > 0 && (
                <span className='px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-md'>{pendingCount} pending</span>
              )}
            </div>

            {(loadingHygiene || loadingCompliance) ? (
              <div className='space-y-2'>
                {[...Array(2)].map((_, i) => <Skeleton key={i} className='h-14 rounded-lg' />)}
              </div>
            ) : allRecs.length === 0 ? (
              <div className='text-center py-6 text-sm text-gray-400 flex flex-col items-center gap-2'>
                <CheckCircle2 className='h-8 w-8 text-green-300' />
                No pending recommendations
              </div>
            ) : (
              <div className='space-y-4'>
                {(hygieneRecs?.recommendations?.length ?? 0) > 0 && (
                  <div>
                    <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5'>
                      <span className='w-2 h-2 rounded-full bg-primary inline-block' />
                      Hygiene
                    </p>
                    <div className='space-y-2'>
                      {hygieneRecs!.recommendations.map(r => (
                        <RecommendationRow key={r.id} rec={r} onToggle={handleToggleResolved} />
                      ))}
                    </div>
                  </div>
                )}
                {(complianceRecs?.recommendations?.length ?? 0) > 0 && (
                  <div>
                    <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5'>
                      <span className='w-2 h-2 rounded-full bg-amber-500 inline-block' />
                      Compliance
                    </p>
                    <div className='space-y-2'>
                      {complianceRecs!.recommendations.map(r => (
                        <RecommendationRow key={r.id} rec={r} onToggle={handleToggleResolved} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* No recommendations at all — add spacer */}
          {!loadingHygiene && !loadingCompliance && allRecs.length === 0 && (
            <div className='flex items-center gap-2 text-xs text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2'>
              <AlertCircle className='h-3.5 w-3.5 flex-shrink-0' />
              All recommendations cleared for this facility.
            </div>
          )}

        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function Facilities() {
  const { dictionary, locale } = useLocale();
  const currentUser = getCurrentUser();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [editingFacility, setEditingFacility] = useState<FacilityDto | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [selectedFacilityTypeId, setSelectedFacilityTypeId] = useState<string | null>(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>('');
  const [filters, setFilters] = useState<{
    geoLocationId?: string;
    facilityTypeId?: string;
    search?: string;
  }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch user's location hierarchy
  const { data: locationHierarchy } = useQuery({
    queryKey: ['user-location-hierarchy'],
    queryFn: () => usersApi.getMyLocationHierarchy(),
    enabled: !!currentUser,
  });

  // Prefill location when form is opened
  useEffect(() => {
    if (showCreateForm && locationHierarchy?.assignedLocationId) {
      setSelectedLocationId(locationHierarchy.assignedLocationId);
    }
  }, [showCreateForm, locationHierarchy]);

  // Auto-set location filter to user's assigned location
  useEffect(() => {
    if (locationHierarchy?.assignedLocationId && !filters.geoLocationId) {
      setFilters(prev => ({
        ...prev,
        geoLocationId: locationHierarchy.assignedLocationId || undefined,
      }));
    }
  }, [locationHierarchy]);

  const { data: facilitiesResponse, isLoading } = useFacilities({
    locationId: filters.geoLocationId,
    facilityTypeId: filters.facilityTypeId,
    page: currentPage,
    limit: itemsPerPage,
    search: filters.search,
  });

  const facilities = facilitiesResponse?.data || [];
  const totalItems = facilitiesResponse?.pagination?.totalItems || facilitiesResponse?.total || 0;
  const totalPages = facilitiesResponse?.pagination?.totalPages || Math.ceil(totalItems / itemsPerPage);

  const { data: facilityTypes } = useFacilityTypes();
  const createFacilityMutation = useCreateFacility();
  const updateFacilityMutation = useUpdateFacility();
  const deactivateFacilityMutation = useDeactivateFacility();

  // Deactivation dialog state
  const [deactivateDialog, setDeactivateDialog] = useState<{
    isOpen: boolean;
    facilityId: string;
    facilityName: string;
  }>({
    isOpen: false,
    facilityId: '',
    facilityName: '',
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
    reset,
    setValue,
    watch,
    clearErrors,
    setError,
  } = useForm<FacilityForm>({
    resolver: zodResolver(facilitySchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  // Watch all fields for error clearing
  const watchedTitle = watch('title');
  const watchedIdentifierNumber = watch('identifierNumber');
  const watchedContactNumber = watch('contactNumber');
  const watchedFacilityTypeId = watch('facilityTypeId');
  const watchedNidNumber = watch('nidNumber');

  // Find the selected facility type object
  const selectedFacilityType = facilityTypes?.find(ft => ft.id === watchedFacilityTypeId);
  const isHomeFacility = selectedFacilityType?.code === 'HOME';

  // Sync identifier with NID when HOME facility type is selected
  useEffect(() => {
    if (isHomeFacility) {
      // Set identifier to NID value (or empty string if NID is empty)
      setValue('identifierNumber', watchedNidNumber || '');
    }
  }, [isHomeFacility, watchedNidNumber, setValue]);

  // Clear errors when user types in fields (only after first submit attempt)
  useEffect(() => {
    if (isSubmitted && watchedTitle && errors.title) {
      clearErrors('title');
    }
  }, [watchedTitle, isSubmitted, errors.title, clearErrors]);

  useEffect(() => {
    if (isSubmitted && watchedIdentifierNumber && errors.identifierNumber) {
      clearErrors('identifierNumber');
    }
  }, [watchedIdentifierNumber, isSubmitted, errors.identifierNumber, clearErrors]);

  useEffect(() => {
    if (isSubmitted && errors.contactNumber) {
      // Clear error if field becomes valid or empty
      if (!watchedContactNumber || watchedContactNumber === '' || /^\+?[\d\s-()]+$/.test(watchedContactNumber)) {
        clearErrors('contactNumber');
      }
    }
  }, [watchedContactNumber, isSubmitted, errors.contactNumber, clearErrors]);

  useEffect(() => {
    if (isSubmitted && errors.nidNumber) {
      // Clear error if field becomes valid or empty
      const isValid =
        !watchedNidNumber ||
        watchedNidNumber === '' ||
        (watchedNidNumber.length === 16 && /^\d{16}$/.test(watchedNidNumber));
      if (isValid) {
        clearErrors('nidNumber');
      }
    }
  }, [watchedNidNumber, isSubmitted, errors.nidNumber, clearErrors]);

  // Check permissions (RBAC)
  const canCreateFacility = hasPermission(currentUser as any, 'FACILITIES_CREATE');
  const canEditFacility = hasPermission(currentUser as any, 'FACILITIES_UPDATE');

  const handleEditClick = (facility: FacilityDto) => {
    setEditingFacility(facility);
    setShowUpdateForm(true)
    setShowCreateForm(false);

    // Pre-populate the form with facility data
    setValue('title', facility.title);
    setValue('identifierNumber', facility.identifierNumber);
    setValue('contactNumber', facility.contactNumber || '');
    setValue('nidNumber', facility.nidNumber || '');
    setValue('email', facility.email || '');
    setValue('facilityTypeId', facility.facilityType?.id || '');
    setValue('locationId', facility.location?.id || '');

    setSelectedFacilityTypeId(facility.facilityType?.id || null);
    setSelectedLocationId(facility.location?.id || '');
    setSelectedSubcategoryId(facility.subcategoryId || '');
  };

  const onSubmit = async (data: FacilityForm) => {
    // Additional validation for HOME facility type - NID is required
    if (isHomeFacility) {
      if (!data.nidNumber || data.nidNumber.trim() === '') {
        setError('nidNumber', {
          type: 'manual',
          message: 'NID is required for home facilities',
        });
        toast.error(
          dictionary['facilities.nid_required_home'] || 'NID is required for home facilities and must be 16 digits'
        );
        return;
      } else if (data.nidNumber.length !== 16) {
        setError('nidNumber', {
          type: 'manual',
          message: 'NID must be exactly 16 digits',
        });
        toast.error(
          dictionary['facilities.nid_required_home'] || 'NID is required for home facilities and must be 16 digits'
        );
        return;
      }
    }

    // Validate facility type is selected
    if (!selectedFacilityTypeId) {
      toast.error(dictionary['facilities.facility_type_required'] || 'Please select a facility type');
      return;
    }

    // If HOME facility, ensure identifier is set to NID
    const identifierValue = isHomeFacility ? data.nidNumber || '' : data.identifierNumber;

    try {
      if (showUpdateForm && editingFacility) {
        // Update existing facility (admin with full permissions)
        const updateData = {
          title: data.title,
          identifierNumber: identifierValue,
          contactNumber: data.contactNumber && data.contactNumber.trim() !== '' ? data.contactNumber : undefined,
          nidNumber: data.nidNumber && data.nidNumber.trim() !== '' ? data.nidNumber : undefined,
          email: data.email && data.email.trim() !== '' ? data.email.trim() : null,
          locationId: selectedLocationId || undefined,
          facilityTypeId: selectedFacilityTypeId,
          subcategoryId: selectedSubcategoryId || null,
        };

        await updateFacilityMutation.mutateAsync({
          id: editingFacility.id,
          data: updateData,
        });
        reset();
        setShowUpdateForm(false);
        setEditingFacility(null);
      } else {
        // Create new facility
        const facilityData: CreateFacilityDto = {
          title: data.title,
          identifierNumber: identifierValue,
          contactNumber: data.contactNumber && data.contactNumber.trim() !== '' ? data.contactNumber : undefined,
          nidNumber: data.nidNumber && data.nidNumber.trim() !== '' ? data.nidNumber : undefined,
          email: data.email && data.email.trim() !== '' ? data.email.trim() : undefined,
          locationId: selectedLocationId || undefined,
          facilityTypeId: selectedFacilityTypeId,
          subcategoryId: selectedSubcategoryId || undefined,
        };

        await createFacilityMutation.mutateAsync(facilityData);
        reset();
        setShowCreateForm(false);
      }
      setSelectedLocationId('');
      setSelectedFacilityTypeId(null);
      setSelectedSubcategoryId('');
    } catch (error) {
      console.error('Facility operation failed:', error);
      toast.error(dictionary['common.error'] || 'An error occurred');
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({});
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== undefined);

  const [selectedFacility, setSelectedFacility] = useState<FacilityDto | null>(null);

  const [isExporting, setIsExporting] = useState(false);
  const handleExport = async () => {
    setIsExporting(true);
    try {
      await facilitiesApi.exportExcel({
        locationId: filters.geoLocationId,
        facilityTypeId: filters.facilityTypeId,
        search: filters.search,
      });
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeactivateFacility = async () => {
    await deactivateFacilityMutation.mutateAsync(deactivateDialog.facilityId);
    setDeactivateDialog({ isOpen: false, facilityId: '', facilityName: '' });
  };

  const columns: TableColumn<FacilityDto>[] = [
    {
      key: 'facilityType',
      label: 'Type',
      sortable: true,
      render: facility =>
        facility.facilityType ? (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800'>
            {locale === 'rw'
              ? facility.facilityType.nameKinyarwanda
              : locale === 'fr'
                ? facility.facilityType.nameFrench
                : facility.facilityType.name}
          </span>
        ) : (
          <span className='text-gray-400 text-sm'>-</span>
        ),
    },
    {
      key: 'title',
      label: 'Facility',
      sortable: true,
      render: facility => (
        <div className='flex items-center gap-2.5'>
          <div className='bg-secondary rounded-lg p-1.5 flex-shrink-0'>
            <Building2 className='h-4 w-4 text-primary' />
          </div>
          <span className='font-medium text-gray-900 whitespace-nowrap'>{facility.title}</span>
        </div>
      ),
    },
    {
      key: 'province',
      label: 'Province',
      render: facility => {
        const name = getLocationLevelName(facility.locationStack?.locationNames, 'PROVINCE', facility.location);
        return name ? <span className='text-sm text-gray-600 whitespace-nowrap'>{name}</span> : <span className='text-gray-400 text-sm'>-</span>;
      },
    },
    {
      key: 'district',
      label: 'District',
      render: facility => {
        const name = getLocationLevelName(facility.locationStack?.locationNames, 'DISTRICT', facility.location);
        return name ? <span className='text-sm text-gray-600 whitespace-nowrap'>{name}</span> : <span className='text-gray-400 text-sm'>-</span>;
      },
    },
    {
      key: 'sector',
      label: 'Sector',
      render: facility => {
        const name = getLocationLevelName(facility.locationStack?.locationNames, 'SECTOR', facility.location);
        return name ? <span className='text-sm text-gray-600 whitespace-nowrap'>{name}</span> : <span className='text-gray-400 text-sm'>-</span>;
      },
    },
    {
      key: 'cell',
      label: 'Cell',
      render: facility => {
        const name = getLocationLevelName(facility.locationStack?.locationNames, 'CELL', facility.location);
        return name ? <span className='text-sm text-gray-600 whitespace-nowrap'>{name}</span> : <span className='text-gray-400 text-sm'>-</span>;
      },
    },
    {
      key: 'village',
      label: 'Village',
      render: facility => {
        const name = getLocationLevelName(facility.locationStack?.locationNames, 'VILLAGE', facility.location);
        return name ? <span className='text-sm text-gray-600 whitespace-nowrap'>{name}</span> : <span className='text-gray-400 text-sm'>-</span>;
      },
    },
    {
      key: 'coordinates',
      label: 'Map',
      render: facility => {
        if (!facility.coordinates) {
          return <span className='text-gray-400 text-sm'>-</span>;
        }
        const [lat, lng] = facility.coordinates.split(',');
        const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
        return (
          <a
            href={googleMapsUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center justify-center w-8 h-8 rounded-lg bg-secondary hover:bg-secondary text-primary transition-colors'
            title='View on map'
          >
            <MapPin className='h-4 w-4' />
          </a>
        );
      },
    },
    {
      key: 'contactNumber',
      label: 'Contact',
      render: facility =>
        facility.contactNumber ? (
          <span className='text-sm text-gray-600'>{facility.contactNumber}</span>
        ) : (
          <span className='text-gray-400 text-sm'>-</span>
        ),
    },
    {
      key: 'nidNumber',
      label: 'NID',
      render: facility =>
        facility.nidNumber ? (
          <span className='text-sm text-gray-600'>{facility.nidNumber}</span>
        ) : (
          <span className='text-gray-400 text-sm'>-</span>
        ),
    },
    {
      key: 'createdAt',
      label: 'Added',
      sortable: true,
      render: facility => (
        <span className='text-sm text-gray-600'>{new Date(facility.createdAt).toLocaleDateString()}</span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: facility => (
        <div className='flex justify-start'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='outline' size='sm' className='h-8 px-2'>
                <MoreVertical className='h-4 w-4' />
                <span className='ml-1 hidden sm:inline'>More</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-48'>
              {canEditFacility && (
                <>
                  <DropdownMenuItem className='gap-2' onClick={() => handleEditClick(facility)}>
                    <Edit className='h-4 w-4' />
                    <span>Edit</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant='destructive'
                    className='gap-2'
                    onClick={() =>
                      setDeactivateDialog({
                        isOpen: true,
                        facilityId: facility.id,
                        facilityName: facility.title,
                      })
                    }
                  >
                    <Trash2 className='h-4 w-4' />
                    <span>Deactivate</span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <div className='p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-[1600px] mx-auto'>
      {/* Header Section */}
      <div className='flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4'>
   
  
      </div>

      {/* Create Form */}
      {showCreateForm && canCreateFacility && (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg flex items-center gap-2'>
              <Building2 className='h-5 w-5 text-primary' />
              {dictionary['facilities.new_facility']}
            </CardTitle>
            <p className='text-sm text-gray-600 mt-1'>{dictionary['facilities.add_facility']}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
              <div>
                <FacilityTypeSelector
                  selectedFacilityTypeId={selectedFacilityTypeId}
                  onFacilityTypeChange={id => {
                    setSelectedFacilityTypeId(id);
                    setValue('facilityTypeId', id || '');
                    setSelectedSubcategoryId('');
                  }}
                  className='mt-2'
                />
              </div>

              {selectedFacilityType && (selectedFacilityType.subcategories?.length ?? 0) > 0 && (
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Subcategory
                  </label>
                  <select
                    value={selectedSubcategoryId}
                    onChange={e => setSelectedSubcategoryId(e.target.value)}
                    className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                  >
                    <option value=''>— None —</option>
                    {selectedFacilityType.subcategories!.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {dictionary['facilities.facility_title']} *
                </label>
                <Input
                  type='text'
                  placeholder={dictionary['facilities.facility_title_placeholder']}
                  {...register('title')}
                  className={errors.title ? 'border-red-300 ring-2 ring-red-100' : ''}
                />
                {errors.title && <p className='mt-1 text-sm text-red-600'>{errors.title.message}</p>}
              </div>

              {!isHomeFacility && (
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {dictionary['facilities.identifier_number']} *
                  </label>
                  <Input
                    type='text'
                    placeholder={dictionary['facilities.identifier_placeholder']}
                    {...register('identifierNumber')}
                    className={errors.identifierNumber ? 'border-red-300 ring-2 ring-red-100' : ''}
                  />
                  {errors.identifierNumber && (
                    <p className='mt-1 text-sm text-red-600'>{errors.identifierNumber.message}</p>
                  )}
                  <p className='mt-1 text-xs text-gray-500'>{dictionary['facilities.identifier_help']}</p>
                </div>
              )}

              {/* Hidden input to store identifier for HOME facility type */}
              {isHomeFacility && (
                <input type='hidden' {...register('identifierNumber')} value={watchedNidNumber || ''} />
              )}

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {dictionary['facilities.contact_number']}
                  </label>
                  <Input
                    type='tel'
                    placeholder={dictionary['facilities.contact_placeholder']}
                    {...register('contactNumber')}
                    className={errors.contactNumber ? 'border-red-300 ring-2 ring-red-100' : ''}
                  />
                  {errors.contactNumber && <p className='mt-1 text-sm text-red-600'>{errors.contactNumber.message}</p>}
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {dictionary['facilities.nid_number']}
                    {isHomeFacility && <span className='text-red-600 ml-1'>*</span>}
                  </label>
                  <Input
                    type='text'
                    placeholder={dictionary['facilities.nid_placeholder']}
                    {...register('nidNumber')}
                    maxLength={16}
                    className={errors.nidNumber ? 'border-red-300 ring-2 ring-red-100' : ''}
                  />
                  {errors.nidNumber && <p className='mt-1 text-sm text-red-600'>{errors.nidNumber.message}</p>}
                  {isHomeFacility && (
                    <p className='mt-1 text-xs text-gray-500'>
                      {dictionary['facilities.nid_required_16'] || 'Required: Must be exactly 16 digits'}
                    </p>
                  )}
                  {!isHomeFacility && (
                    <p className='mt-1 text-xs text-gray-500'>
                      {dictionary['facilities.nid_optional_16'] || 'Optional: Must be 16 digits if provided'}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {dictionary['facilities.email'] || 'Facility Email'}
                </label>
                <Input
                  type='email'
                  placeholder={dictionary['facilities.email_placeholder'] || 'facility@example.com'}
                  {...register('email')}
                  className={errors.email ? 'border-red-300 ring-2 ring-red-100' : ''}
                />
                {errors.email && <p className='mt-1 text-sm text-red-600'>{errors.email.message}</p>}
                <p className='mt-1 text-xs text-gray-500'>
                  {dictionary['facilities.email_help'] || 'Assessment reports will be sent to this address'}
                </p>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {dictionary['facilities.location_selection']}
                </label>
                <SingleLocationSelector
                  selectedLocationId={selectedLocationId}
                  onLocationChange={id => setSelectedLocationId(id || '')}
                  initialHierarchy={
                    locationHierarchy
                      ? {
                          provinceId: locationHierarchy.provinceId,
                          districtId: locationHierarchy.districtId,
                          sectorId: locationHierarchy.sectorId,
                          cellId: locationHierarchy.cellId,
                          villageId: locationHierarchy.villageId,
                        }
                      : undefined
                  }
                  allowChildrenOnly={true}
                  disabled={!!locationHierarchy?.villageId}
                />
                {locationHierarchy?.villageId ? (
                  <p className='mt-1 text-xs text-gray-500'>{dictionary['facilities.location_locked']}</p>
                ) : (
                  <p className='mt-1 text-xs text-gray-500'>{dictionary['facilities.location_children_only']}</p>
                )}
              </div>

              <div className='flex gap-2 pt-2'>
                <Button type='submit' disabled={createFacilityMutation.isPending}>
                  {createFacilityMutation.isPending
                    ? dictionary['facilities.creating']
                    : dictionary['facilities.create']}
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => {
                    setShowCreateForm(false);
                    reset();
                  }}
                >
                  {dictionary['form.cancel']}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Update Form */}
      {showUpdateForm && canEditFacility && editingFacility && (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg flex items-center gap-2'>
              <Edit className='h-5 w-5 text-primary' />
              Update Facility
            </CardTitle>
            <p className='text-sm text-gray-600 mt-1'>Update facility information.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
              {/* Title */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {dictionary['facilities.facility_title']} *
                </label>
                <Input
                  type='text'
                  placeholder={dictionary['facilities.facility_title_placeholder']}
                  {...register('title')}
                  className={errors.title ? 'border-red-300 ring-2 ring-red-100' : ''}
                />
                {errors.title && <p className='mt-1 text-sm text-red-600'>{errors.title.message}</p>}
              </div>

              {/* Facility Type */}
              <div>
                <FacilityTypeSelector
                  selectedFacilityTypeId={selectedFacilityTypeId}
                  onFacilityTypeChange={id => {
                    setSelectedFacilityTypeId(id);
                    setValue('facilityTypeId', id || '');
                    setSelectedSubcategoryId('');
                  }}
                  className='mt-2'
                />
              </div>

              {selectedFacilityType && (selectedFacilityType.subcategories?.length ?? 0) > 0 && (
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Subcategory
                  </label>
                  <select
                    value={selectedSubcategoryId}
                    onChange={e => setSelectedSubcategoryId(e.target.value)}
                    className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                  >
                    <option value=''>— None —</option>
                    {selectedFacilityType.subcategories!.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Identifier Number - conditional on facility type */}
              {!isHomeFacility && (
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {dictionary['facilities.identifier_number']} *
                  </label>
                  <Input
                    type='text'
                    placeholder={dictionary['facilities.identifier_placeholder']}
                    {...register('identifierNumber')}
                    className={errors.identifierNumber ? 'border-red-300 ring-2 ring-red-100' : ''}
                  />
                  {errors.identifierNumber && (
                    <p className='mt-1 text-sm text-red-600'>{errors.identifierNumber.message}</p>
                  )}
                  <p className='mt-1 text-xs text-gray-500'>{dictionary['facilities.identifier_help']}</p>
                </div>
              )}

              {isHomeFacility && (
                <input type='hidden' {...register('identifierNumber')} value={watchedNidNumber || ''} />
              )}

              {/* Contact Number and NID */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {dictionary['facilities.contact_number']}
                  </label>
                  <Input
                    type='tel'
                    placeholder={dictionary['facilities.contact_placeholder']}
                    {...register('contactNumber')}
                    className={errors.contactNumber ? 'border-red-300 ring-2 ring-red-100' : ''}
                  />
                  {errors.contactNumber && <p className='mt-1 text-sm text-red-600'>{errors.contactNumber.message}</p>}
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {dictionary['facilities.nid_number']}
                    {isHomeFacility && <span className='text-red-600 ml-1'>*</span>}
                  </label>
                  <Input
                    type='text'
                    placeholder={dictionary['facilities.nid_placeholder']}
                    {...register('nidNumber')}
                    maxLength={16}
                    className={errors.nidNumber ? 'border-red-300 ring-2 ring-red-100' : ''}
                  />
                  {errors.nidNumber && <p className='mt-1 text-sm text-red-600'>{errors.nidNumber.message}</p>}
                  {isHomeFacility && (
                    <p className='mt-1 text-xs text-gray-500'>
                      {dictionary['facilities.nid_required_16'] || 'Required: Must be exactly 16 digits'}
                    </p>
                  )}
                  {!isHomeFacility && (
                    <p className='mt-1 text-xs text-gray-500'>
                      {dictionary['facilities.nid_optional_16'] || 'Optional: Must be 16 digits if provided'}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {dictionary['facilities.email'] || 'Facility Email'}
                </label>
                <Input
                  type='email'
                  placeholder={dictionary['facilities.email_placeholder'] || 'facility@example.com'}
                  {...register('email')}
                  className={errors.email ? 'border-red-300 ring-2 ring-red-100' : ''}
                />
                {errors.email && <p className='mt-1 text-sm text-red-600'>{errors.email.message}</p>}
                <p className='mt-1 text-xs text-gray-500'>
                  {dictionary['facilities.email_help'] || 'Assessment reports will be sent to this address'}
                </p>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {dictionary['facilities.location_selection']}
                </label>
                <SingleLocationSelector
                  selectedLocationId={selectedLocationId}
                  onLocationChange={id => setSelectedLocationId(id || '')}
                  allowChildrenOnly={false}
                />
              </div>

              <div className='flex gap-2 pt-2'>
                <Button type='submit' disabled={updateFacilityMutation.isPending}>
                  {updateFacilityMutation.isPending ? 'Updating...' : 'Update Facility'}
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => {
                    setShowUpdateForm(false);
                    setEditingFacility(null);
                    reset();
                  }}
                >
                  {dictionary['form.cancel']}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Facilities List Section */}
      <div>
        <div>
          <div className='flex flex-col sm:flex-row items-start mb-2 sm:items-center justify-between gap-3'>
            <div>
              <CardTitle className='text-lg'>{dictionary['facilities.all_facilities']}</CardTitle>
              <p className='text-sm text-gray-600 mt-1'>{dictionary['facilities.browse_search']}</p>
            </div>
            <div className='flex items-center gap-2'>

            {canCreateFacility && (
              <Button
                onClick={() => {
                  setShowCreateForm(!showCreateForm);
                  setShowUpdateForm(false);
                  setEditingFacility(null);
                  reset();
                }}
                className='w-full sm:w-auto'
              >
                <Plus className='h-4 w-4 mr-2' />
                {dictionary['facilities.create_facility']}
              </Button>
            )}
            <Button
              variant='outline'
              onClick={() => setShowFilters(!showFilters)}
              className='flex items-center gap-2 w-full sm:w-auto justify-center'
            >
              <Filter className='h-4 w-4' />
              {showFilters ? dictionary['facilities.hide_filters'] : dictionary['facilities.show_filters']}
              {hasActiveFilters && (
                <span className='ml-1 px-2 py-0.5 text-xs bg-primary text-white rounded-md'>
                  {dictionary['facilities.active_filters']}
                </span>
              )}
            </Button>
            <Button
              variant='outline'
              onClick={handleExport}
              disabled={isExporting}
              className='flex items-center gap-2 w-full sm:w-auto justify-center'
            >
              <Download className='h-4 w-4' />
              {isExporting ? 'Exporting…' : 'Export Excel'}
            </Button>
            </div>

          </div>
        </div>

        <div>
          {/* Filters Panel */}
          {showFilters && (
            <div className='mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200'>
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-sm font-medium text-gray-900'>{dictionary['facilities.filters_title']}</h3>
                {hasActiveFilters && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={clearFilters}
                    className='flex items-center gap-1 text-xs'
                  >
                    <X className='h-3 w-3' />
                    {dictionary['facilities.clear_all']}
                  </Button>
                )}
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {/* Location Filter */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    {dictionary['facilities.filter_by_location']}
                  </label>
                  <SingleLocationSelector
                    selectedLocationId={filters.geoLocationId}
                    onLocationChange={id => handleFilterChange('geoLocationId', id)}
                  />
                </div>

                {/* Facility Type Filter */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    {dictionary['dashboard.facility_type']}
                  </label>
                  <select
                    value={filters.facilityTypeId || ''}
                    onChange={e => handleFilterChange('facilityTypeId', e.target.value)}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
                  >
                    <option value=''>{dictionary['facilities.all_facility_types']}</option>
                    {facilityTypes?.map(type => (
                      <option key={type.id} value={type.id}>
                        {locale === 'rw' ? type.nameKinyarwanda : locale === 'fr' ? type.nameFrench : type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
          {/* <Input
            type='text'
            placeholder={dictionary['facilities.search_placeholder']}
            value={filters.search}
            onChange={e => handleFilterChange('search', e.target.value)}
            className='mb-4'
          /> */}

          <Table
            columns={columns}
            data={facilities}
            isLoading={isLoading}
            enablePagination={false}
            searchPlaceholder={dictionary['facilities.search_placeholder']}
            emptyMessage={
              hasActiveFilters
                ? dictionary['facilities.no_facilities_filters']
                : dictionary['facilities.no_facilities_found']
            }
            showHeader={!showFilters}
            onRowClick={facility => setSelectedFacility(facility)}
            filters={
              showFilters ? (
                <div className='flex items-center gap-2'>
                  {hasActiveFilters && (
                    <Button variant='outline' size='sm' onClick={clearFilters} className='flex items-center gap-1'>
                      <X className='h-3 w-3' />
                      Clear Filters
                    </Button>
                  )}
                </div>
              ) : undefined
            }
          />

          {/* API-based Pagination */}
          {totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={limit => {
                setItemsPerPage(limit);
                setCurrentPage(1); // Reset to first page when changing items per page
              }}
              showItemsPerPage={true}
              itemsPerPageOptions={[10, 20, 50, 100]}
              translations={{
                showing: dictionary['common.showing'],
                to: dictionary['common.to'],
                of: dictionary['common.of'],
                results: dictionary['common.results'],
                itemsPerPage: dictionary['common.items_per_page'],
                previous: dictionary['common.previous'],
                next: dictionary['common.next'],
                firstPage: dictionary['common.first_page'],
                lastPage: dictionary['common.last_page'],
                goToPage: dictionary['common.go_to_page'],
              }}
            />
          )}
        </div>
      </div>

      {/* Facility Detail Drawer */}
      {selectedFacility && (
        <FacilityDrawer
          facility={selectedFacility}
          onClose={() => setSelectedFacility(null)}
          locale={locale}
        />
      )}

      {/* Deactivate Confirmation Dialog */}
      <ConfirmDialog
        open={deactivateDialog.isOpen}
        onOpenChange={open => !open && setDeactivateDialog({ isOpen: false, facilityId: '', facilityName: '' })}
        onConfirm={handleDeactivateFacility}
        title='Deactivate Facility'
        description={`Are you sure you want to deactivate "${deactivateDialog.facilityName}"? This action cannot be undone.`}
        confirmText='Deactivate'
        variant='danger'
        isLoading={deactivateFacilityMutation.isPending}
      />
    </div>
  );
}
